import React, { useEffect } from 'react';
import T from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Grid, Button } from '@mui/material';
import { StyledContent, StyledHandlers, StyledNewDialog } from '../elements';
import { updateMachineAutomationSystemConfig } from 'redux/machines/actions';
import { IrisV3InViewConfigurationSchema } from 'attrs/formValidation';
import { Formik } from 'formik';
import { FormElements } from 'web-components';
import { useSelector, useDispatch } from 'react-redux';
import { getAutomationSystemConfig } from 'redux/machines/selectors';
import { loadMachineAutomationSystemConfig } from '../../../../redux/machines/actions';
import { SUCCESS } from 'attrs/status';

const EditAutomationSystemConfigurationIRISV3 = ({ open, handleClose, machineId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: automationSystemConfig, loadingState: automationSystemConfigLoadingState } =
    useSelector(getAutomationSystemConfig);

  useEffect(() => {
    dispatch(loadMachineAutomationSystemConfig(machineId));
  }, [dispatch, machineId]);

  const handleUpdateAutomationSystemConfiguration = values => {
    const iris_v3_inview_payload = {
      iris_v3_inview: {
        database_name: values.database_name,
        variables_table_name: values.variables_table_name
      },
    };
    dispatch(updateMachineAutomationSystemConfig(machineId, iris_v3_inview_payload));
    handleClose();
  };

  if (automationSystemConfigLoadingState.status !== SUCCESS) return null;

  return (
    <Formik
      initialValues={{
        database_name: automationSystemConfig.iris_v3_inview.database_name,
        variables_table_name: automationSystemConfig.iris_v3_inview.variables_table_name
      }}
      validationSchema={IrisV3InViewConfigurationSchema}
      onSubmit={handleUpdateAutomationSystemConfiguration}
    >
      {({ values, handleChange, handleBlur, handleSubmit, errors }) => {
        const actionBar = (
          <StyledHandlers>
            <Button color="secondary" onClick={handleClose}>
              {t('form.cancel')}
            </Button>
            <Button
              variant="contained"
              style={{ padding: '1rem 2.5rem' }}
              onClick={handleSubmit}
              disabled={errors.database_name || errors.variables_table_name}
            >
              {t('form.save')}
            </Button>
          </StyledHandlers>
        );

        return (
          <StyledNewDialog
            open={open}
            handleClose={handleClose}
            title={t('machines.detail.automation_system.configuration.edit_automation_system_configuration')}
            subTitle={t('machines.detail.automation_system.configuration.subtitle')}
            maxWidth="1062px"
            displayActionBar="true"
            actionBar={actionBar}
            content={
              <StyledContent>
                <form>
                  <Grid container spacing={2} columns={12}>
                    <Grid item xs={6}>
                      <FormElements.TextField
                        fullWidth
                        label={t('machines.detail.automation_system.configuration.database_name')}
                        id="database_name"
                        margin="normal"
                        name="database_name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.database_name}
                        required
                        type="text"
                        error={errors.database_name}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormElements.TextField
                        fullWidth
                        label={t('machines.detail.automation_system.configuration.variable_table_name')}
                        id="variables_table_name"
                        margin="normal"
                        name="variables_table_name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.variables_table_name}
                        required
                        type="text"
                        error={errors.variables_table_name}
                      />
                    </Grid>
                  </Grid>
                </form>
              </StyledContent>
            }
          />
        );
      }}
    </Formik>
  );
};

EditAutomationSystemConfigurationIRISV3.propTypes = {
  open: T.bool.isRequired,
  handleClose: T.func.isRequired,
  machineId: T.string.isRequired,
  automationSystem: T.shape({
    plc_type: T.string
  }).isRequired
};

export default EditAutomationSystemConfigurationIRISV3;
